import { AddIcon } from '@chakra-ui/icons';
import { FormControl, FormLabel, HStack, IconButton } from '@chakra-ui/react';
import { Select } from 'chakra-react-select';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BsThreeDotsVertical } from 'react-icons/bs';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import useTRSClusters from './hooks/useTRSClusters';
import useGetTRSNodes from './queries/useGetTRSNodes';

import { Button, Text } from '@/components/v4';
import { dropDownFormStyle } from 'components/forms/new/formStyles';
import { Error } from 'components/index';
import ActionsCellWrapper from 'components/tables/AGGrid/ActionsCellWrapper';
import AGGridMain from 'components/tables/AGGrid/AGGridMain';
import CopyCellRenderer from 'components/tables/AGGrid/CustomCells/CopyCellRenderer';
import Tooltip from 'components/v4/Tooltip';

export default function TRSNodes() {
    const gridRef = useRef();
    const navigate = useNavigate();
    const location = useLocation();
    const { clusterID = '' } = location.state || {};

    const { darkMode } = useSelector((state) => state.settings);
    const { currentCompany } = useSelector((state) => state.navigationLists);

    const [selectedCluster, setSelectedCluster] = useState(null);
    const [tableData, setTableData] = useState({
        columns: [],
        data: [],
    });

    // Fetch clusters for filter dropdown
    const {
        data: clusters = [],
        isLoading: clustersLoading,
        error: clustersError,
    } = useTRSClusters();

    const handleEditNode = useCallback(
        (nodeData) => {
            navigate(`/trs-nodes/edit/${nodeData.id}`, {
                state: {
                    clusterID: selectedCluster?.id,
                },
            });
        },
        [navigate, selectedCluster?.id],
    );

    const colDefs = useMemo(
        () => [
            {
                headerName: 'Name',
                field: 'name',
                cellRenderer: CopyCellRenderer,
                cellStyle: () => ({ width: '100%' }),
                flex: 1,
                minWidth: 150,
            },
            {
                headerName: 'Cluster Name',
                field: 'clusterName',
                cellRenderer: CopyCellRenderer,
                valueGetter: (params) => params.data.clusterName,
                flex: 1,
                minWidth: 200,
            },
            {
                headerName: 'Capacity',
                field: 'capacity',
                cellRenderer: CopyCellRenderer,
                valueGetter: (params) => params.data.capacity,
                flex: 1,
                minWidth: 120,
            },
            {
                maxWidth: 70,
                filter: false,
                suppressMovable: true,
                lockVisible: true,
                suppressHeaderMenuButton: true,
                suppressHeaderFilterButton: true,
                enableRowGroup: false,
                resizable: false,
                sortable: false,
                cellStyle: () => ({
                    display: 'flex',
                    alignItems: 'center',
                }),
                lockPosition: 'right',
                cellRenderer: (params) => {
                    return (
                        <ActionsCellWrapper>
                            <HStack
                                spacing={0}
                                justifyContent="center"
                                alignItems="center"
                                width="100%"
                                height="100%">
                                <Tooltip label="Edit Node">
                                    <IconButton
                                        icon={
                                            <BsThreeDotsVertical
                                                fill={
                                                    darkMode ? 'white' : 'black'
                                                }
                                            />
                                        }
                                        aria-label="Edit"
                                        variant="ghost"
                                        onClick={() => {
                                            handleEditNode(params.data);
                                        }}
                                    />
                                </Tooltip>
                            </HStack>
                        </ActionsCellWrapper>
                    );
                },
            },
        ],
        [darkMode, handleEditNode],
    );

    const { refetch, isRefetching, isFetching } = useGetTRSNodes(
        setTableData,
        gridRef,
        colDefs,
        selectedCluster,
    );

    const isFetchingData = isRefetching || isFetching || clustersLoading;

    const titleName = 'TRS Nodes';

    useEffect(() => {
        if (clusters.length > 0 && !selectedCluster) {
            if (clusterID) {
                const preSelectedCluster = clusters.find(
                    (c) => c.id === clusterID,
                );
                setSelectedCluster(preSelectedCluster);
            } else {
                setSelectedCluster(clusters[0]);
            }
        }
    }, [clusters, selectedCluster, clusterID]);

    const handleClusterChange = (selectedOption) => {
        setSelectedCluster(selectedOption);
    };

    const handleAddNode = () => {
        navigate('/trs-nodes/add', {
            state: { clusterID: selectedCluster?.id },
        });
    };

    if (!currentCompany) {
        return (
            <Error
                error={{
                    name: 'No Partner / Company / Wholesaler Selected',
                    message:
                        'Please select a wholesaler and a partner / company.',
                }}
            />
        );
    }

    if (clustersError) {
        return (
            <Error
                error={{
                    name: 'Failed to load clusters',
                    message: 'Please refresh the page to try again.',
                }}
            />
        );
    }

    return (
        <>
            <HStack justifyContent="space-between" marginBottom={5}>
                <Text sx={{ fontSize: '20', fontWeight: '500' }}>
                    TRS Nodes
                </Text>
            </HStack>

            <AGGridMain
                gridRef={gridRef}
                tableData={tableData}
                isLoading={isFetchingData}
                titleName={titleName}
                refetch={refetch}
                extraComponents={
                    <HStack pl={5} spacing={2}>
                        <Button
                            minW="110px"
                            leftIcon={<AddIcon />}
                            onClick={handleAddNode}
                            size="sm"
                            padding="6px 12px">
                            Add Node
                        </Button>

                        {/* Cluster Filter */}
                        <FormControl className="!ml-[16px]">
                            <HStack justifyContent="center" w="500px">
                                <FormLabel
                                    className="w-1/6 leading-4"
                                    fontSize="sm"
                                    mt={2}
                                    mr={0}>
                                    Filter by Cluster:
                                </FormLabel>
                                <Select
                                    className="ml-0 w-full"
                                    placeholder="Select cluster"
                                    isLoading={clustersLoading}
                                    value={selectedCluster}
                                    options={clusters}
                                    getOptionLabel={(option) => option.name}
                                    getOptionValue={(option) => option.id}
                                    onChange={handleClusterChange}
                                    isClearable={false}
                                    chakraStyles={dropDownFormStyle(darkMode)}
                                    selectedOptionColor="brand"
                                />
                            </HStack>
                        </FormControl>
                    </HStack>
                }
            />
        </>
    );
}
